import {useFluxStore} from 'client/framework';
import React, {useEffect, useState} from 'react';

import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import sessionStore from 'client/Session/SessionStore';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import {local} from 'lib/StorageUtil';

import {GetStartedModule} from './Home/GetStartedModule';
import {Home} from './Home/Home';
import {isAutoArchiveSeason} from './Home/autoArchiveSeason';
import {TeacherDashboardLayout} from './Layout/TeacherDashboardLayout';
import {useTeacherClassroomActions} from './hooks/useTeacherClassrooms';
import {useTeacherDashboardStore} from './useTeacherDashboardStore';

interface Props {
  location?: {
    pathname: string;
  };
}

/**
 * Teacher Home owns the Get Started rail. On wide screens it's a persistent side panel whose collapsed
 * state is remembered (localStorage). On narrow screens (< m) it's an overlay that defaults closed — so
 * it never covers the content on load and re-closes whenever we drop to narrow
 */
export const TeacherDashboard = ({location}: Props) => {
  const pathname = location?.pathname ?? '/home';
  const userId = useFluxStore(sessionStore, (s) => s.getUserId());
  const storageKey = `${userId}-homeHideGetStarted`;
  const [collapsed, setCollapsed] = useState<boolean>(() => Boolean(local.get(storageKey)));
  const [narrowRailOpen, setNarrowRailOpen] = useState(false);

  // Below the `m` breakpoint (1020px) the rail can't sit beside the content, so it becomes an overlay.
  // matchMedia flips only when the breakpoint is actually crossed — no per-pixel resize re-render.
  const {isMAndUp} = useBreakpoint();
  const isNarrow = !isMAndUp;

  // Home renders one of two states based on active (non-archived) classes. Narrow selectors returning
  // stable primitives: a full classroom-list replacement (Clever sync, create-class refetch) shouldn't
  // re-render the whole shell, only flip these when emptiness / first-load actually changes.
  const hasActiveClasses = useTeacherDashboardStore((s) => !s.classrooms.isEmpty());
  const hasLoadedClassrooms = useTeacherDashboardStore((s) => s.hasLoadedClassrooms);
  const archivedClassroomsLoadedFor = useTeacherDashboardStore(
    (s) => s.archivedClassroomsLoadedFor
  );
  const areArchivedClassroomsLoading = useTeacherDashboardStore(
    (s) => s.areArchivedClassroomsLoading
  );
  const {fetchArchivedClassrooms} = useTeacherClassroomActions();
  // The masqueraded (effective) id — the archived list is fetched for this identity, not the admin's own.
  const effectiveUserId = useFluxStore(
    masqueradeStore,
    (s) => s.getUserIdByMasqueradeState() ?? ''
  );

  // Computed once per mount: the season window won't cross mid-session, and reading the time-offset
  // store during render is an untracked external read the React Compiler can't track. Reused in both
  // the fetch effect and the gate so they can't disagree across a season-boundary tick.
  const [isArchiveSeason] = useState(isAutoArchiveSeason);
  const isArchivedLoadedForUser = archivedClassroomsLoadedFor === effectiveUserId;

  // During auto-archive season the "classes were archived" banner needs the archived list, which is
  // otherwise fetched lazily. Load it with the initial page load (keyed by identity, so a masquerade
  // switch re-fetches) and gate the first paint on it (below) so the banner appears with the rest of the
  // page instead of popping in a few seconds late (#19169).
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- season-gated initial-load fetch */
  useEffect(() => {
    if (isArchiveSeason && !isArchivedLoadedForUser && !areArchivedClassroomsLoading) {
      fetchArchivedClassrooms();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount + identity change only
  }, [effectiveUserId]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */

  const isInitialLoad = !hasLoadedClassrooms || (isArchiveSeason && !isArchivedLoadedForUser);

  const updateCollapsed = (next: boolean) => {
    local.set(storageKey, next);
    setCollapsed(next);
  };

  // `collapsed` is the saved open/closed preference; `narrowRailOpen` only keeps the overlay from
  // auto-covering content on a fresh narrow load. Both sync on every toggle, so an intentionally closed
  // rail stays closed (and an open one stays open) when the viewport crosses the breakpoint.
  const railOpen = isNarrow ? narrowRailOpen : !collapsed;
  const openRail = () => {
    updateCollapsed(false);
    setNarrowRailOpen(true);
  };
  const closeRail = () => {
    updateCollapsed(true);
    setNarrowRailOpen(false);
  };

  return (
    <TeacherDashboardLayout
      pathname={pathname}
      rightRail={
        hasActiveClasses ? (
          <GetStartedModule onDismiss={closeRail} open={railOpen} overlay={isNarrow} />
        ) : undefined
      }
    >
      <Home
        getStartedCollapsed={!railOpen}
        hasActiveClasses={hasActiveClasses}
        isInitialLoad={isInitialLoad}
        onReopenGetStarted={openRail}
      />
    </TeacherDashboardLayout>
  );
};
